/**
 * NativeTitleTooltip — upgrades browser-native `title` tooltips to styled
 * app tooltips (same look/size as MUI / Manage Jobs) for the whole app.
 *
 * Strips `title` → `data-app-title` eagerly (and on DOM changes) so the OS
 * tooltip never appears (including sort-arrow `title="edit"`).
 *
 * Added for 962.Harsha using AI Agent. October 19, 2026.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/* Empty titles are ignored; all other title= values (including "edit") get custom tooltips. */
const SKIP_TITLES = new Set(['']);

/* Titles longer than this are almost never intentional hover help (e.g. full job descriptions). */
const MAX_TOOLTIP_TITLE_LENGTH = 200;

/**
 * CKEditor / Froala / oversized HTML titles — not intentional app hover help.
 * @param {string} text
 * @returns {boolean}
 */
function isEditorChromeTitle(text) {
	const t = String(text || '').trim().toLowerCase();
	if (!t) {
		return true;
	}
	if (SKIP_TITLES.has(t)) {
		return true;
	}
	/* e.g. "Rich Text Editor, editor1" / "Rich Text Editor, editor3" */
	if (t.indexOf('rich text editor') !== -1) {
		return true;
	}
	if (/^editor\s*\d+$/i.test(t)) {
		return true;
	}
	/* Full job descriptions / HTML blobs accidentally put on title= */
	if (t.length > MAX_TOOLTIP_TITLE_LENGTH) {
		return true;
	}
	if (t.indexOf('<') !== -1 && t.indexOf('>') !== -1) {
		return true;
	}
	return false;
}

/**
 * Skip CKEditor / Froala chrome / iframes / contenteditable — not app hover tooltips.
 * @param {Element} el
 * @returns {boolean}
 */
function isEditorChromeElement(el) {
	if (!el || el.nodeType !== 1) {
		return true;
	}
	const tag = el.tagName;
	if (tag === 'IFRAME' || tag === 'title' || tag === 'TITLE') {
		return true;
	}
	if (el.isContentEditable) {
		return true;
	}
	if (el.classList) {
		const cls = el.className && String(el.className);
		if (
			cls &&
			(cls.indexOf('cke_') !== -1 ||
				cls.indexOf('ck-') !== -1 ||
				cls.indexOf('fr-') !== -1)
		) {
			return true;
		}
	}
	if (
		el.closest &&
		el.closest(
			'.cke, .cke_chrome, .cke_inner, .cke_contents, .ck, .ck-editor, .fr-box, .fr-wrapper, .fr-toolbar, .fr-popup, .fr-modal'
		)
	) {
		return true;
	}
	return false;
}

/**
 * Move title → data-app-title so the browser never shows a native tooltip.
 * @param {Element} el
 */
function stripNativeTitle(el) {
	if (!el || el.nodeType !== 1 || !el.hasAttribute('title')) {
		return;
	}
	if (isEditorChromeElement(el)) {
		return;
	}
	const text = String(el.getAttribute('title') || '').trim();
	if (isEditorChromeTitle(text)) {
		return;
	}
	el.setAttribute('data-app-title', text);
	el.removeAttribute('title');
	if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
		el.setAttribute('aria-label', text);
	}
}

/**
 * Strip native titles under a root (document or a newly added subtree).
 * @param {ParentNode} root
 */
function stripTitlesInTree(root) {
	if (!root || !root.querySelectorAll) {
		return;
	}
	if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('title')) {
		stripNativeTitle(root);
	}
	const nodes = root.querySelectorAll('[title]');
	for (let i = 0; i < nodes.length; i++) {
		stripNativeTitle(nodes[i]);
	}
}

/**
 * Walk up from the event target to find an element with hover-help text.
 * @param {EventTarget|null} node
 * @returns {{ el: Element, text: string }|null}
 */
function findTitleHost(node) {
	let el = node && node.nodeType === 1 ? node : node && node.parentElement;
	while (el && el !== document.body && el !== document.documentElement) {
		if (isEditorChromeElement(el)) {
			return null;
		}
		if (
			el.classList &&
			(el.classList.contains('app-native-title-tooltip') ||
				el.classList.contains('MuiTooltip-popper') ||
				el.classList.contains('MuiTooltip-tooltip'))
		) {
			return null;
		}

		/* Always strip any leftover native title on the way up (nested Filter cases). */
		if (el.hasAttribute('title')) {
			stripNativeTitle(el);
		}

		const raw = el.getAttribute('data-app-title');
		if (raw != null) {
			const text = String(raw).trim();
			if (!isEditorChromeTitle(text)) {
				return { el, text };
			}
		}
		el = el.parentElement;
	}
	return null;
}

function NativeTitleTooltip() {
	const [tip, setTip] = useState({ open: false, text: '', left: 0, top: 0 });
	const activeElRef = useRef(null);
	const hideTimerRef = useRef(null);

	useEffect(() => {
		/* Eagerly remove native titles so the browser tooltip never fires. */
		stripTitlesInTree(document.body);

		const observer = new MutationObserver((mutations) => {
			for (let i = 0; i < mutations.length; i++) {
				const mutation = mutations[i];
				if (mutation.type === 'attributes' && mutation.attributeName === 'title') {
					stripNativeTitle(mutation.target);
				}
				if (mutation.type === 'childList') {
					for (let j = 0; j < mutation.addedNodes.length; j++) {
						const node = mutation.addedNodes[j];
						if (node.nodeType === 1) {
							stripTitlesInTree(node);
						}
					}
				}
			}
		});
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['title']
		});

		const clearHideTimer = () => {
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
				hideTimerRef.current = null;
			}
		};

		const hide = () => {
			clearHideTimer();
			activeElRef.current = null;
			setTip((prev) => (prev.open ? { ...prev, open: false } : prev));
		};

		const positionFor = (el, text) => {
			const rect = el.getBoundingClientRect();
			setTip({
				open: true,
				text,
				left: rect.left + rect.width / 2,
				top: rect.bottom + 8
			});
		};

		const showFor = (el, text) => {
			clearHideTimer();
			/* Strip this host and any nested/parent titles that could still fire natively. */
			stripNativeTitle(el);
			if (el.querySelectorAll) {
				const nested = el.querySelectorAll('[title]');
				for (let i = 0; i < nested.length; i++) {
					stripNativeTitle(nested[i]);
				}
			}
			if (el.parentElement) {
				stripNativeTitle(el.parentElement);
			}
			activeElRef.current = el;
			positionFor(el, text);
		};

		const onMouseOver = (event) => {
			const found = findTitleHost(event.target);
			if (!found) {
				return;
			}
			if (activeElRef.current === found.el) {
				return;
			}
			showFor(found.el, found.text);
		};

		const onMouseOut = (event) => {
			if (!activeElRef.current) {
				return;
			}
			const related = event.relatedTarget;
			if (related && activeElRef.current.contains(related)) {
				return;
			}
			clearHideTimer();
			hideTimerRef.current = setTimeout(() => {
				const under = document.elementFromPoint(event.clientX, event.clientY);
				const still = under ? findTitleHost(under) : null;
				if (still && still.el === activeElRef.current) {
					return;
				}
				hide();
			}, 80);
		};

		const onScrollOrResize = () => {
			if (activeElRef.current) {
				hide();
			}
		};

		document.addEventListener('mouseover', onMouseOver, true);
		document.addEventListener('mouseout', onMouseOut, true);
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);

		return () => {
			clearHideTimer();
			observer.disconnect();
			document.removeEventListener('mouseover', onMouseOver, true);
			document.removeEventListener('mouseout', onMouseOut, true);
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, []);

	if (!tip.open || !tip.text) {
		return null;
	}

	return ReactDOM.createPortal(
		<div
			className="app-native-title-tooltip"
			role="tooltip"
			style={{
				position: 'fixed',
				left: tip.left,
				top: tip.top,
				transform: 'translate(-50%, 0)',
				pointerEvents: 'none',
				zIndex: 1500
			}}
		>
			<span className="app-native-title-tooltip-arrow" aria-hidden="true" />
			{tip.text}
		</div>,
		document.body
	);
}

export default NativeTitleTooltip;
